import type { CreateVehicleTO } from '@control-center/control-center-client-public-vehicles-ts';

import type { SimulatedVehicleCreation } from './simulatedVehicleCreationNotifications';
import type { SimulationConfig } from './vehicle-dialog/CreateVehicleForm';

interface CreatedVehicle {
    vin: string;
}

export class ContainerBackendCreationError extends Error {
    readonly vehicle: CreatedVehicle;
    readonly simulationConfig: SimulationConfig;

    constructor(vehicle: CreatedVehicle, simulationConfig: SimulationConfig, cause: unknown) {
        super('Vehicle was created, but its container backend could not be created', { cause });
        this.name = 'ContainerBackendCreationError';
        this.vehicle = vehicle;
        this.simulationConfig = simulationConfig;
    }
}

interface CreateVehicleWithContainerBackendOptions {
    vehicle: CreateVehicleTO;
    simulationConfig?: SimulationConfig;
    createVehicle: (vehicle: CreateVehicleTO) => Promise<CreatedVehicle>;
    createContainerBackend: (vin: string, simulationConfig: SimulationConfig) => Promise<SimulatedVehicleCreation>;
}

export const createVehicleWithContainerBackend = async ({
    vehicle,
    simulationConfig,
    createVehicle,
    createContainerBackend,
}: CreateVehicleWithContainerBackendOptions) => {
    const createdVehicle = await createVehicle(vehicle);
    if (vehicle.subtype !== 'SIMULATED_VEHICLE_WITH_CONNECTIVITY' || !simulationConfig) {
        return { vehicle: createdVehicle, simulatedVehicleCreation: undefined };
    }

    try {
        return {
            vehicle: createdVehicle,
            simulatedVehicleCreation: await createContainerBackend(createdVehicle.vin, simulationConfig),
        };
    } catch (error) {
        throw new ContainerBackendCreationError(createdVehicle, simulationConfig, error);
    }
};
